import type { Editor } from '@tiptap/core'
import { useEffect, useReducer } from 'react'
import styled from 'styled-components'

import Icon from '../../general/Icon'
import { AaFormattingDropdown } from './AaFormattingDropdown'
import { BlockStyleDropdown } from './BlockStyleDropdown'

type Props = {
  editor: Editor
}

/**
 * Toolbar for TipTap — parity with legacy Lexical toolbar (undo/redo, block style
 * incl. lists, B/I/U, link, “Aa” extra marks). Styled with theme tokens.
 */
export function TipTapToolbar({ editor }: Props) {
  const [, rerender] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    const handle = () => rerender()
    editor.on('selectionUpdate', handle)
    editor.on('transaction', handle)
    return () => {
      editor.off('selectionUpdate', handle)
      editor.off('transaction', handle)
    }
  }, [editor])

  const canUndo = editor.can().undo()
  const canRedo = editor.can().redo()

  const setOrUnsetLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined
    if (editor.isActive('link')) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    const url = window.prompt('Link URL', previous ?? 'https://')
    if (url === null) {
      return
    }
    const trimmed = url.trim()
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: trimmed })
      .run()
  }

  return (
    <Toolbar role="toolbar" aria-label="Formatting">
      <ToolbarButton
        type="button"
        title="Undo"
        disabled={!canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Icon name="UndoOutlined" />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        title="Redo"
        disabled={!canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Icon name="RedoOutlined" />
      </ToolbarButton>

      <ToolbarSeparator aria-hidden />
      <BlockStyleDropdown editor={editor} />
      <ToolbarSeparator aria-hidden />

      <ToolbarButton
        type="button"
        title="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <strong>B</strong>
      </ToolbarButton>
      <ToolbarButton
        type="button"
        title="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <em>I</em>
      </ToolbarButton>
      <ToolbarButton
        type="button"
        title="Underline"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineSpan>U</UnderlineSpan>
      </ToolbarButton>

      <ToolbarSeparator aria-hidden />
      <ToolbarButton
        type="button"
        title={editor.isActive('link') ? 'Remove link' : 'Add link'}
        $active={editor.isActive('link')}
        onClick={setOrUnsetLink}
      >
        <Icon name="LinkOutlined" />
      </ToolbarButton>
      <AaFormattingDropdown editor={editor} />
    </Toolbar>
  )
}

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  background: ${({ theme }) => theme.color.neutral.color10};
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color7};
  gap: 4px;
  padding: 8px;
`

const ToolbarButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) =>
    $active ? 'rgba(0, 0, 0, 0.08)' : 'transparent'};
  border-radius: 4px;
  border: 1px solid transparent;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font: inherit;
  justify-content: center;
  min-width: 28px;
  padding: 4px 8px;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`
const ToolbarSeparator = styled.span`
  background: ${({ theme }) => theme.color.neutral.color7};
  display: inline-block;
  height: 20px;
  margin: 0 4px;
  width: 1px;
`

const UnderlineSpan = styled.span`
  text-decoration: underline;
`
